import type {
  MultiSelectResponse,
  QuestionResponse,
  RenderableQuestion,
  SingleSelectResponse,
  TextResponse,
} from '@assessments/shared'
import { RichText } from '@/components/ui/RichText'
import { QuestionHelperText } from './QuestionHelperText.js'
import { SingleSelectQuestion } from './SingleSelectQuestion.js'
import { MultiSelectQuestion } from './MultiSelectQuestion.js'
import { WriteInQuestion } from './WriteInQuestion.js'
import { EssayQuestion } from './EssayQuestion.js'
import { IndustrySelectQuestion } from './IndustrySelectQuestion.js'
import type { SaveStatus } from './useSaveResponse.js'

interface Props {
  question: RenderableQuestion
  /** The respondent's existing answer, if any. */
  response?: QuestionResponse
  onChange: (response: QuestionResponse) => void
  /** This question's last save outcome; only 'error' renders anything. */
  saveStatus?: SaveStatus
  disabled?: boolean
  /** Required and currently listed in the last blocked Mark Complete attempt's
   * unanswered set — distinct from the always-on `*` required marker. */
  flagged?: boolean
}

const SINGLE_EMPTY: SingleSelectResponse = { kind: 'single', selectedOptionId: null }
const MULTI_EMPTY: MultiSelectResponse = { kind: 'multi', selectedOptionIds: [] }
const TEXT_EMPTY: TextResponse = { kind: 'text', text: '' }

/** Renders a stored answer as read-only text — used for retired/unknown types (FR-011/012). */
function storedAnswerText(response: QuestionResponse | undefined): string {
  if (!response) return 'No saved answer'
  switch (response.kind) {
    case 'single':
      return response.selectedOptionId ?? 'No saved answer'
    case 'multi':
      return response.selectedOptionIds.join(', ') || 'No saved answer'
    case 'text':
      return response.text || 'No saved answer'
    case 'industry':
      return response.subIndustryId ?? 'No saved answer'
  }
}

/**
 * Renders one question: label + helper text, then the input control for its type.
 * An unrecognised/retired type renders read-only with any stored answer and never
 * throws — so historical questions of retired types can't break the section or lose
 * data (FR-011/012).
 */
export function QuestionRenderer({
  question,
  response,
  onChange,
  saveStatus,
  disabled,
  flagged,
}: Props) {
  const labelId = `q-label-${question.id}`
  const helperId = question.helperText ? `q-helper-${question.id}` : undefined
  const describedBy = helperId

  return (
    // A stable, focusable target for "jump to first" — tabIndex=-1 keeps it out of
    // normal Tab order while still allowing a programmatic .focus() by id.
    <div id={`question-${question.id}`} tabIndex={-1} className="py-4 outline-none">
      <div id={labelId} className="flex flex-wrap items-baseline text-sm">
        <RichText html={question.label} className="font-medium text-body" />
        {question.required && (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        )}
        {flagged && (
          <span className="ml-2 text-xs font-medium text-destructive">Needs an answer</span>
        )}
      </div>
      <QuestionHelperText id={helperId} text={question.helperText} />

      {question.type === 'single_select' && (
        <SingleSelectQuestion
          question={question}
          value={response?.kind === 'single' ? response : SINGLE_EMPTY}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
          disabled={disabled}
        />
      )}

      {question.type === 'multi_select' && (
        <MultiSelectQuestion
          question={question}
          value={response?.kind === 'multi' ? response : MULTI_EMPTY}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
          disabled={disabled}
        />
      )}

      {question.type === 'write_in' && (
        <WriteInQuestion
          value={response?.kind === 'text' ? response : TEXT_EMPTY}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
          disabled={disabled}
        />
      )}

      {question.type === 'essay' && (
        <EssayQuestion
          value={response?.kind === 'text' ? response : TEXT_EMPTY}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
          disabled={disabled}
        />
      )}

      {question.type === 'industry' && (
        <IndustrySelectQuestion
          question={question}
          onChange={onChange}
          labelledBy={labelId}
          describedBy={describedBy}
          disabled={disabled}
        />
      )}

      {question.type === 'unknown' && (
        <p className="mt-3 rounded-button border border-input bg-surface-hover px-3 py-2 text-sm text-subtle">
          This question type is no longer supported and is shown for reference only.
          <span className="mt-1 block text-body">{storedAnswerText(response)}</span>
        </p>
      )}

      {saveStatus === 'error' && (
        <p role="alert" className="mt-2 flex items-center gap-3 text-sm text-danger">
          Couldn’t save your answer.
          {response && (
            <button
              type="button"
              onClick={() => onChange(response)}
              className="font-medium underline hover:no-underline"
            >
              Retry
            </button>
          )}
        </p>
      )}
    </div>
  )
}
